import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Phone, Wrench, Zap, Home as HomeIcon, BadgeCheck, Play } from "lucide-react";

const float = (d = 0) => ({
  animate: { y: [0, -8, 0] },
  transition: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: d },
});

export default function OfferMockup() {
  return (
    <div className="relative mx-auto w-full max-w-[620px] pb-16 sm:pb-10 lg:pr-6" data-testid="hero-mockup">
      <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#FF6A00]/25 via-transparent to-[#0066FF]/25 blur-3xl pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 30, rotate: -1 }} animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }} className="relative">
        <BrowserFrame />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
        className="absolute -bottom-2 right-0 sm:-right-2 w-[34%] sm:w-[30%] max-w-[180px]">
        <motion.div {...float(0.5)}><PhoneFrame /></motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8 }}
        className="absolute -top-4 left-4 sm:-left-4" data-testid="hero-chip-delivery">
        <motion.div {...float(0)} className="flex items-center gap-2 bg-white text-black rounded-full pl-1.5 pr-3.5 py-1.5 shadow-2xl text-xs font-semibold">
          <span className="w-6 h-6 rounded-full bg-[#22C55E] flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></span>
          Live in 7 days
        </motion.div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1 }}
        className="absolute bottom-6 sm:bottom-10 left-2 sm:-left-6" data-testid="hero-chip-price">
        <motion.div {...float(1.2)} className="bg-[#0A0A0A]/90 backdrop-blur-xl border border-white/10 rounded-2xl px-4 py-3 shadow-2xl">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Starter Site</div>
          <div className="font-display text-2xl font-bold tracking-tight text-white">$199 <span className="text-xs font-normal text-white/50">one-time</span></div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function BrowserFrame() {
  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]">
      <div className="h-9 bg-neutral-100 flex items-center gap-1.5 px-4 border-b border-black/5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
        <div className="ml-4 flex-1 max-w-[260px] h-5 rounded-md bg-white border border-black/5 text-[10px] text-neutral-400 font-mono flex items-center px-2">yourbusiness.com</div>
      </div>
      <div className="text-black">
        <div className="flex items-center justify-between px-5 py-3 border-b border-black/5">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-md bg-[#050505]" />
            <span className="font-display font-bold text-sm tracking-tight">Your Business</span>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-[11px] text-neutral-500">
            <span>Services</span><span>About</span><span>Reviews</span>
            <span className="inline-flex items-center gap-1 bg-[#FF6A00] text-white rounded-full px-2.5 py-1 font-semibold"><Phone className="w-3 h-3" /> Call Now</span>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-4 px-5 py-6 bg-gradient-to-br from-neutral-50 to-white">
          <div className="col-span-3">
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#FF6A00]">Licensed · Local · Trusted</div>
            <div className="mt-2 font-display font-bold text-lg sm:text-2xl leading-[1.05] tracking-tight">Reliable service for your home.</div>
            <div className="mt-2 h-2 w-11/12 rounded bg-neutral-200" />
            <div className="mt-1.5 h-2 w-3/4 rounded bg-neutral-200" />
            <div className="mt-4 flex gap-2">
              <span className="text-[10px] sm:text-[11px] font-semibold bg-[#050505] text-white rounded-full px-3 py-1.5">Get a Free Quote</span>
              <span className="text-[10px] sm:text-[11px] font-semibold border border-black/15 rounded-full px-3 py-1.5">Our Services</span>
            </div>
          </div>
          <div className="col-span-2 rounded-xl border border-black/5 bg-white shadow-lg p-3 space-y-2">
            <div className="text-[10px] font-semibold">Request a quote</div>
            {[0, 1, 2].map((i) => <div key={i} className="h-5 rounded-md bg-neutral-100 border border-black/5" />)}
            <div className="h-6 rounded-md bg-[#FF6A00]" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 px-5 pb-5">
          {[Wrench, Zap, HomeIcon].map((Icon, i) => (
            <div key={i} className="rounded-lg border border-black/5 bg-neutral-50 p-3">
              <Icon className="w-4 h-4 text-[#0066FF]" />
              <div className="mt-2 h-2 w-3/4 rounded bg-neutral-200" />
              <div className="mt-1 h-2 w-1/2 rounded bg-neutral-200" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Real client testimonial video. Lightweight thumbnail first; YouTube loads only on tap (faster LCP/INP).
function PhoneFrame() {
  const VIDEO_ID = "5nAL-HXQ-W4";
  const [playing, setPlaying] = useState(false);
  const src = `https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
  return (
    <div className="relative rounded-[1.6rem] bg-[#0A0A0A] border border-white/15 p-1.5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]" data-testid="hero-testimonial-card">
      <div className="relative aspect-[9/19] rounded-[1.25rem] overflow-hidden bg-black">
        {playing ? (
          <iframe src={src} title="WebsiteAndGo client testimonial video" className="absolute inset-0 w-full h-full" frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="hero-testimonial-iframe" />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} data-testid="hero-testimonial-play"
            aria-label="Play client testimonial video" className="group absolute inset-0 w-full h-full">
            <img src={`https://i.ytimg.com/vi/${VIDEO_ID}/oar2.jpg`} alt="WebsiteAndGo client testimonial video thumbnail"
              width="360" height="640" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-11 h-11 rounded-full bg-[#FF6A00] flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                <Play className="w-4 h-4 text-black fill-black ml-0.5" />
              </span>
            </span>
          </button>
        )}
        <div className="absolute top-2 left-2 right-2 flex justify-center pointer-events-none">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur text-[8px] font-mono uppercase tracking-[0.15em] text-white/85">
            <BadgeCheck className="w-2.5 h-2.5 text-[#FF6A00]" /> Real client
          </span>
        </div>
      </div>
    </div>
  );
}
